import { createMongoAbility } from '@casl/ability';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { Loading } from '#components/loading.js';
import { useTRPC } from '#src/trpc.js';
import { auth0UserQueryOptions } from '#utils/auth0-user-query-options.js';
import { abilityOverrideAtom } from '../atoms/ability-override-atom.js';
import { AbilityContext, AbilityWithoutOverrideContext } from './ability-context.js';

export type AbilityContextProviderProps = {
	children: React.ReactNode;
};

export function AbilityContextProvider({ children }: AbilityContextProviderProps) {
	const trpc = useTRPC();

	const { data: auth0User } = useSuspenseQuery(auth0UserQueryOptions);

	const { data: currentUser, isPending: currentUserIsPending } = useQuery(
		trpc.user.getCurrent.queryOptions(undefined, {
			enabled: auth0User !== null,
			throwOnError: auth0User !== null,
		}),
	);
	const abilityWithoutOverride = useMemo(
		() => (currentUser === undefined ? undefined : createMongoAbility(currentUser.auth.rules)),
		[currentUser],
	);

	const abilityOverride = useAtomValue(abilityOverrideAtom);
	const abilityOverrideAbility = useMemo(
		() => (abilityOverride.rules ? createMongoAbility(abilityOverride.rules) : undefined),
		[abilityOverride.rules],
	);

	const abilityWithOverride = abilityOverrideAbility ?? abilityWithoutOverride;

	if (currentUserIsPending && auth0User !== null) {
		return <Loading />;
	}

	return (
		<AbilityContext value={abilityWithOverride ?? emptyAbility}>
			<AbilityWithoutOverrideContext value={abilityWithoutOverride ?? emptyAbility}>
				{children}
			</AbilityWithoutOverrideContext>
		</AbilityContext>
	);
}

const emptyAbility = createMongoAbility([]);
